import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import PasswordShowEye from "../../components/icons/PasswordShowEye";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getAccountBootstrap,
  updateAccountAvatar,
  updateAccountPassword,
  updateAccountProfile,
} from "../../api/account";
import { AccountProfile, AccountTab } from "../../interfaces/account";
import {
  userPasswordUpdateEvent,
  userPrefsTabEvent,
  userPrefsUpdateEvent,
} from "../../components/mixPanelEvents/mixPanelEvents";

type ActiveTab = "none" | "manage" | "password";

type MobileDrillIn = "none" | "manage" | "password";

/** Matches theme `$grid-breakpoints` md / `userprefs.js` (768px). */
const BP_MD_UP = "@media (min-width: 768px)";
/** Manage-form stack breakpoint: 2-col from 992px+, 1-col below. */
const BP_LG_UP = "@media (min-width: 992px)";

const REPRO_STACK = '"Repro", sans-serif';
dayjs.extend(customParseFormat);

/** Replaces Bootstrap `.sr-only` for the home crumb label. */
const visuallyHidden: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const handOptions = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
];

const handicapOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
    { value: "5", label: "5" },
  { value: "6", label: "6" },
    { value: "7", label: "7" },
        { value: "8", label: "8" }, 
           { value: "9", label: "9" },
           { value: "10", label: "10" },
           { value: "11", label: "11" },
           { value: "12", label: "12" },
           { value: "13", label: "13" },
           { value: "14", label: "14" },
           { value: "15", label: "15" },

];

const feetOptions = [
  { value: "4", label: "4" },
    { value: "5", label: "5" },
  { value: "6", label: "6" },
    { value: "7", label: "7" },
];

const inchesOptions = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
    { value: "3", label: "3" },
  { value: "4", label: "4" },
    { value: "5", label: "5" },
  { value: "6", label: "6" },
    { value: "7", label: "7" },
  { value: "8", label: "8" },
    { value: "9", label: "9" },
  { value: "10", label: "10" },
  { value: "11", label: "11" },
];

const REQUIRED_MANAGE_FIELDS: { key: "first_name" | "last_name" | "user_dob" | "user_hand" | "user_handicap"; id: string }[] = [
  { key: "first_name", id: "account-first-name" },
  { key: "last_name", id: "account-last-name" },
  { key: "user_dob", id: "account-dob" },
  { key: "user_hand", id: "account-hand" },
  { key: "user_handicap", id: "account-handicap" },
];



/** Absolute corner label — same layout as `CornerPasswordField`. */
const cornerFieldLabelSx = {
  boxSizing: "border-box" as const,
  position: "absolute" as const,
  left: "10px",
  top: "6px",
  zIndex: 1,
  display: "block",
  height: "12px",
  width: "fit-content",
  fontFamily: REPRO_STACK,
  fontSize: "10px",
  fontWeight: 400,
  lineHeight: "12px",
  textAlign: "start" as const,
  textTransform: "uppercase" as const,
  color: "rgb(123, 114, 108)",
  cursor: "default",
  pointerEvents: "none" as const,
};

const cornerOutlinedInputPaddingSx = {
  borderRadius: "8px",
  "& .MuiOutlinedInput-input": {
    paddingTop: "22px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
};

function normalizeDobForDisplay(rawValue: string): string {
  const raw = (rawValue || "").trim();
  if (!raw) return "";
  const slash = dayjs(raw, "MM/DD/YYYY", true);
  if (slash.isValid()) return slash.format("MM/DD/YYYY");
  const iso = dayjs(raw, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format("MM/DD/YYYY");
  return raw;
}

function parseDobToDayjs(rawValue: string): Dayjs | null {
  const normalized = normalizeDobForDisplay(rawValue);
  if (!normalized) return null;
  const parsed = dayjs(normalized, "MM/DD/YYYY", true);
  return parsed.isValid() ? parsed : null;
}

/** Turn relative / protocol-relative avatar URLs into absolute so <img> and canvas load reliably. */
function resolveMediaUrl(url: string): string {
  const raw = (url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:")) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (typeof window === "undefined") return raw;
  if (raw.startsWith("//")) {
    return `${window.location.protocol}${raw}`;
  }
  if (raw.startsWith("/")) {
    let origin = window.location.origin;
    try {
      const root = (window as Window & { apiSettings?: { root?: string } }).apiSettings?.root;
      if (root) {
        origin = new URL(root, window.location.href).origin;
      }
    } catch {
      /* ignore */
    }
    return `${origin}${raw}`;
  }
  return raw;
}

/** WordPress `site_url()`-style home, derived from `rest_url()` in `window.apiSettings.root`. */
function getSiteHomeUrl(): string {
  if (typeof window === "undefined") return "/";
  const restRoot = (window as Window & { apiSettings?: { root?: string } }).apiSettings?.root;
  if (restRoot) {
    try {
      const u = new URL(restRoot, window.location.href);
      let path = u.pathname.replace(/\/+$/, "");
      if (path.endsWith("/wp-json")) {
        path = path.slice(0, -"/wp-json".length) || "/";
      }
      const trail = path === "" || path === "/" ? "/" : path.endsWith("/") ? path : `${path}/`;
      return `${u.origin}${trail}`;
    } catch {
      /* ignore */
    }
  }
  return `${window.location.origin}/`;
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") resolve(result);
      else reject(new Error("Failed to read file"));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function CornerPasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
  showValid,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggleShow: () => void;
  showValid?: boolean;
}) {
  return (
    <FormControl fullWidth variant="outlined" sx={{ position: "relative" }}>
      <Box component="label" htmlFor={id} sx={cornerFieldLabelSx}>
        {label}
      </Box>
      <OutlinedInput
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        required
        endAdornment={
          <InputAdornment position="end" sx={{ mr: "2px" }}>
            <IconButton
              aria-label="Toggle password visibility"
              edge="end"
              size="small"
              onClick={onToggleShow}
              onMouseDown={(e: React.MouseEvent<HTMLElement>) => e.preventDefault()}
              sx={{ color: "rgb(123, 114, 108)" }}
            >
              {show ? <VisibilityOff fontSize="small" /> : <PasswordShowEye />}
            </IconButton>
            {showValid && (
              <Box
                component="img"
                src={resolveMediaUrl("/wp-content/themes/pgm/dist/images/icons/validation-check.svg")}
                alt="valid"
                sx={{ width: "18px", height: "18px", ml: "4px" }}
              />
            )}
          </InputAdornment>
        }
        sx={{
          borderRadius: "4px",
          "& .MuiOutlinedInput-input": {
            paddingTop: "22px",
            paddingBottom: "10px",
            paddingLeft: "10px",
            paddingRight: "8px",
          },
        }}
      />
    </FormControl>
  );
}

/** Outlined text / date / select with the same corner label as `CornerPasswordField`. */
function CornerOutlineField({
  id,
  label,
  value,
  onChange,
  required,
  type = "text",
  select,
  datePicker,
  children,
  error,
  helperText,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  select?: boolean;
  datePicker?: boolean;
  children?: React.ReactNode;
  error?: boolean;
  helperText?: string;
}) {
  const errorBorderColor = "#D32F2F";

  if (select) {
    return (
      <FormControl fullWidth variant="outlined" sx={{ position: "relative", border:"none" }}>
        <Box component="label" htmlFor={id} sx={cornerFieldLabelSx}>
          {label}
        </Box>
 <Select
 IconComponent={(props) => (
  <img {...props} style={{marginRight: 10, height:12, width:12}} src="https://dev-alt-members.performancegolf.com/wp-content/uploads/2026/04/icon.svg" alt="arrow-down" />
 )}
  displayEmpty
  value={value}
  onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
  input={<OutlinedInput id={id} notched={false} />}
  required={required}
  error={error}
  aria-describedby={helperText ? `${id}-helper-text` : undefined}
  sx={{
        "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
        border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
    borderRadius: "8px",
    color: "#7B726C",
    "& .MuiSelect-select": {
      paddingTop: "22px",
      paddingBottom: "10px",
      paddingLeft: "10px",
      paddingRight: "32px"
    },
  }}
  MenuProps={{
    PaperProps: {
      sx: {
        maxHeight: 180,
        overflowY: "auto",
        borderRadius: "8px",
        border: "1px solid #B3AAA3",
        boxShadow: "none",
        color: "#7B726C",

        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: "#F4F2F0 !important",
        },
        "& .MuiMenuItem-root.Mui-selected:hover": {
          backgroundColor: "#F4F2F0 !important",
        }
      }
    },
    MenuListProps: {
      sx: {
        padding: 0,
      }
    }
  }}
>
  {children}
</Select>
        {helperText && (
          <FormHelperText id={`${id}-helper-text`} error={error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  if (datePicker) {
    return (
  <FormControl fullWidth variant="outlined" sx={{ position: "relative" }}>
  <Box component="label" htmlFor={id} sx={cornerFieldLabelSx}>
    {label}
  </Box>

  <DatePicker
    format="MM/DD/YYYY"
    value={parseDobToDayjs(value)}
    onChange={(newValue) =>
      onChange(newValue?.isValid() ? newValue.format("MM/DD/YYYY") : "")
    }
    sx={{ width: "100%" }}
    slotProps={{
      textField: {
        id,
        required,
        error,
        "aria-describedby": helperText ? `${id}-helper-text` : undefined,
        fullWidth: true,
        placeholder: "mm. dd. yyyy.",
        sx: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            color: "#7B726C",

            "& .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
            },

            "& .MuiInputBase-input": {
              paddingTop: "22px",
              paddingBottom: "10px",
              paddingLeft: "10px",
              paddingRight: "32px",
            },
          },
        },
      },
    }}
  />
  {helperText && (
    <FormHelperText id={`${id}-helper-text`} error={error}>
      {helperText}
    </FormHelperText>
  )}
</FormControl>
    );
  }

  return (
    <FormControl fullWidth variant="outlined" sx={{ position: "relative" }}>
      <Box component="label" htmlFor={id} sx={cornerFieldLabelSx}>
        {label}
      </Box>
      <OutlinedInput
        id={id}
        type={type}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        required={required}
        error={error}
        aria-describedby={helperText ? `${id}-helper-text` : undefined}
        sx={{
    ...cornerOutlinedInputPaddingSx,
        color: "#7B726C",

    "& .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${error ? errorBorderColor : "#B3AAA3"}`,
  },
  }}
      />
      {helperText && (
        <FormHelperText id={`${id}-helper-text`} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function AvatarEditorDialog({
  open,
  avatar: _avatar,
  onClose,
  onSaved,
}: {
  open: boolean;
  avatar: string;
  onClose: () => void;
  onSaved: (avatarUrl: string) => void;
}) {
  const [sourceDataUrl, setSourceDataUrl] = useState<string>("");
  const [zoom, setZoom] = useState<number>(1);
  const [error, setError] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [cameraOn, setCameraOn] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isNewUpload = Boolean(sourceDataUrl && sourceDataUrl.startsWith("data:"));

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleSelectFile = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file.");
      return;
    }
    setError("");
    setZoom(1);
    setSourceDataUrl(await readFileAsDataURL(file));
    setCameraOn(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const startCamera = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {
            setError("Unable to start camera preview.");
          });
        }
      }, 0);
    } catch (e) {
      setError("Camera is blocked or unavailable.");
    }
  };

  const captureFromCamera = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 320;
    canvas.height = video.videoHeight || 240;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    setZoom(1);
    setSourceDataUrl(dataUrl);
    setCameraOn(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const getCroppedBase64 = async (): Promise<string | null> => {
    if (!sourceDataUrl) return null;
    const img = new Image();
    img.src = sourceDataUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to load image."));
    });

    const targetSize = 400;
    const canvas = document.createElement("canvas");
    canvas.width = targetSize;
    canvas.height = targetSize;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const z = sourceDataUrl.startsWith("data:") ? zoom : 1;
    const drawWidth = img.width * z;
    const drawHeight = img.height * z;
    const x = (targetSize - drawWidth) / 2;
    const y = (targetSize - drawHeight) / 2;
    ctx.drawImage(img, x, y, drawWidth, drawHeight);
    return canvas.toDataURL("image/jpeg", 0.92);
  };

  const saveAvatar = async () => {
    try {
      setBusy(true);
      setError("");
      const base64 = await getCroppedBase64();
      if (!base64) {
        setError("Please upload or capture an image first.");
        setBusy(false);
        return;
      }
      const res = await updateAccountAvatar(base64);
      const avatarUrl = res?.data?.avatar || res?.data?.data?.avatar;
      if (!avatarUrl) {
        throw new Error("Failed to save avatar.");
      }
      userPrefsUpdateEvent(["avatar"]);
      onSaved(avatarUrl);
      onClose();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Failed to save avatar.");
    } finally {
      setBusy(false);
    }
  };

  const previewStyle = useMemo(
    () => ({
      width: 200,
      height: 200,
      borderRadius: "50%",
      overflow: "hidden",
      border: "1px solid #ddd",
      margin: "0 auto",
      position: "relative" as const,
      backgroundColor: "#f2f2f2",
    }),
    []
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxWidth: "680px",
          minHeight: "432px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          bgcolor: "#fff",
          p: 0,
        },
        className: "pg-popup-app-profile-pic",
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          px: 0,
          pb: 0,
          pt: 0,
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          bgcolor: "#fff",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            right: 45,
            top: 45,
            border: "none",
            background: "transparent",
            width: 14,
            height: 14,
            padding: 0,
            cursor: "pointer",
            backgroundImage: 'url("/wp-content/themes/pgm/dist/images/icons/x-v2.svg")',
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "14px 14px",
          }}
        >
        </button>
        <Stack spacing={0}>
          <Typography
            sx={{
              textAlign: "center",
              mt: "80px",
              mb: "25px",
              px: "15px",
              fontSize: "30px",
              lineHeight: 1.1,
              textTransform: "uppercase",
              fontWeight: 500,
              color: "#000",
            }}
          >
            Upload Profile Picture
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}

          {cameraOn ? (
            <Box sx={{ textAlign: "center" }}>
              <video ref={videoRef} style={{ width: "100%", maxHeight: 320 }} />
              <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  onClick={captureFromCamera}
                  sx={{ borderWidth: "2px", borderStyle: "solid", borderColor: "#FD3300" }}
                >
                  Take A Picture
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCameraOn(false);
                    if (streamRef.current) {
                      streamRef.current.getTracks().forEach((track) => track.stop());
                      streamRef.current = null;
                    }
                  }}
                  sx={{
                    borderWidth: "2px",
                    borderStyle: "solid",
                    "&:hover": { borderWidth: "2px" },
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              {!sourceDataUrl ? (
                <Box
                  onDragOver={(e: React.DragEvent<HTMLElement>) => e.preventDefault()}
                  onDrop={(e: React.DragEvent<HTMLElement>) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0] ?? null;
                    handleSelectFile(file);
                  }}
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "400px",
                      mx: "auto",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/wp-content/themes/pgm/dist/images/icons/profile-circle-big.svg"
                      alt=""
                      width={80}
                      height={80}
                      style={{ display: "block" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      mt: "25px",
                      mb: "25px",
                      fontSize: 16,
                      lineHeight: "30px",
                      fontWeight: 400,
                      color: "#000",
                    }}
                  >
                    Drag photo here
                  </Typography>
                  <Typography
                    sx={{
                      textAlign: "center",
                      mb: "25px",
                      fontSize: 18,
                      lineHeight: "22px",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    - or -
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={previewStyle}
                    onDragOver={(e: React.DragEvent<HTMLElement>) => e.preventDefault()}
                    onDrop={(e: React.DragEvent<HTMLElement>) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0] ?? null;
                      handleSelectFile(file);
                    }}
                  >
                    <img
                      key={sourceDataUrl.startsWith("data:") ? `data:${sourceDataUrl.length}` : sourceDataUrl}
                      src={sourceDataUrl}
                      alt="avatar preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transform: isNewUpload ? `scale(${zoom})` : "none",
                        transformOrigin: "center center",
                      }}
                    />
                  </Box>
                  {isNewUpload && (
                    <Box sx={{ px: { xs: 1, md: 4 }, maxWidth: 400, mx: "auto", width: "100%" }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          mb: 0.5,
                          color: "#333",
                        }}
                      >
                        Zoom
                      </Typography>
                      <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.01}
                        onChange={(_, value) => setZoom(Array.isArray(value) ? value[0] : value)}
                        sx={{
                          color: "#FD3300",
                          "& .MuiSlider-thumb": { backgroundColor: "#FD3300" },
                          "& .MuiSlider-track": { backgroundColor: "#FD3300", borderColor: "#FD3300" },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}

              {!sourceDataUrl ? (
                <Box
                  sx={{
                    mt: "30px",
                    mx: "25px",
                    mb: "40px",
                    [BP_MD_UP]: {
                      mt: 0,
                      mx: "40px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1.5rem",
                      [BP_MD_UP]: {
                        flexDirection: "row",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%" } }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          backgroundColor: "#FD3300",
                          borderWidth: "2px",
                          borderStyle: "solid",
                          borderColor: "#FD3300",
                          borderRadius: "9999px",
                          minHeight: 48,
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: "87%",
                          py: "15px",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#DB2C00",
                            borderColor: "#DB2C00",
                            borderWidth: "2px",
                          },
                        }}
                      >
                        Upload From Computer
                      </Button>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%" } }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={startCamera}
                        sx={{
                          borderRadius: "9999px",
                          borderWidth: "2px",
                          borderStyle: "solid",
                          minHeight: 48,
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: "87%",
                          py: "15px",
                          px: 3,
                          textTransform: "none",
                          borderColor: "#000",
                          color: "#000",
                          backgroundColor: "#fff",
                          "&:hover": {
                            borderColor: "#000",
                            borderWidth: "2px",
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        Take A Picture
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: "30px",
                    mx: "25px",
                    mb: "40px",
                    [BP_MD_UP]: {
                      mt: 0,
                      mx: "40px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1.5rem",
                      [BP_MD_UP]: {
                        flexDirection: "row",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%" } }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setSourceDataUrl("");
                          setZoom(1);
                        }}
                        sx={{
                          borderRadius: "9999px",
                          borderWidth: "2px",
                          borderStyle: "solid",
                          minHeight: 48,
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: "87%",
                          py: "15px",
                          textTransform: "none",
                          borderColor: "#7B726C",
                          color: "#7B726C",
                          backgroundColor: "transparent",
                          "&:hover": {
                            borderColor: "#7B726C",
                            borderWidth: "2px",
                            backgroundColor: "#f7f7f7",
                          },
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%" } }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={saveAvatar}
                        disabled={busy}
                        sx={{
                          backgroundColor: "#FD3300",
                          borderWidth: "2px",
                          borderStyle: "solid",
                          borderColor: "#FD3300",
                          borderRadius: "9999px",
                          minHeight: 48,
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: "87%",
                          py: "15px",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#DB2C00",
                            borderColor: "#DB2C00",
                            borderWidth: "2px",
                          },
                        }}
                      >
                        {busy ? "Saving..." : "Saves"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/gif,image/jpeg"
                style={{ display: "none" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSelectFile(e.target.files?.[0] ?? null)}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function AccountPage() {
  const outerTheme = useTheme();
  const reproTheme = useMemo(
    () =>
      createTheme(outerTheme, {
        typography: {
          fontFamily: REPRO_STACK,
          allVariants: { fontFamily: REPRO_STACK },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                fontFamily: REPRO_STACK,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ECE9E4",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#7B726C",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ECE9E4",
                  borderWidth: "1px",
                },
              },
              input: { fontFamily: REPRO_STACK },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
              message: { fontFamily: REPRO_STACK },
            },
          },
          MuiBreadcrumbs: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
              li: { fontFamily: REPRO_STACK },
              separator: { fontFamily: REPRO_STACK },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: { fontFamily: REPRO_STACK },
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: { fontFamily: REPRO_STACK },
            },
          },
          MuiSlider: {
            styleOverrides: {
              valueLabel: { fontFamily: REPRO_STACK },
              markLabel: { fontFamily: REPRO_STACK },
            },
          },
        },
      }),
    [outerTheme]
  );

  const isMobile = useMediaQuery(outerTheme.breakpoints.down(768));
  const [mobileDrillIn, setMobileDrillIn] = useState<MobileDrillIn>("none");

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [tabs, setTabs] = useState<AccountTab[]>([]);
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [feet, setFeet] = useState("");
const [inches, setInches] = useState("");
  const [manageForm, setManageForm] = useState({
    first_name: "",
    last_name: "",
    user_height: "",
    user_dob: "",
    user_hand: "",
    user_handicap: "",
  });
  const [initialManageForm, setInitialManageForm] = useState({
    first_name: "",
    last_name: "",
    user_height: "",
    user_dob: "",
    user_hand: "",
    user_handicap: "",
  });
  const [busyManage, setBusyManage] = useState<boolean>(false);
  const [manageErrors, setManageErrors] = useState<Record<string, string>>({});
  const [busyPassword, setBusyPassword] = useState<boolean>(false);
  const [passwordForm, setPasswordForm] = useState({ password: "", password2: "" });
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [openAvatar, setOpenAvatar] = useState<boolean>(false);
  const [avatarModalNonce, setAvatarModalNonce] = useState(0);


const getHeightParts = () => {
  const match = manageForm.user_height.match(/(\d+)'\s*(\d+)"?/);
  return {
    feetValue: match ? match[1] : "0",
    inchesValue: match ? match[2] : "0",
  };
};

const handleFeetChange = (newFeet: string) => {
  const { inchesValue } = getHeightParts();

  const newHeight = `${newFeet}' ${inchesValue}"`;

  setManageForm(prev => ({
    ...prev,
    user_height: newHeight
  }));
};

const handleInchesChange = (newInches: string) => {
  const { feetValue } = getHeightParts();

  const newHeight = `${feetValue}' ${newInches}"`;

  setManageForm(prev => ({
    ...prev,
    user_height: newHeight
  }));
};

const { feetValue, inchesValue } = getHeightParts();



  const activeTab: ActiveTab = location.pathname.includes("/account/password")
    ? "password"
    : "manage";

  useEffect(() => {
    if (!isMobile) {
      setMobileDrillIn("none");
      return;
    }
    if (location.pathname.includes("/account/password")) {
      setMobileDrillIn("password");
      return;
    }
    if (location.pathname.includes("/account/manage")) {
      setMobileDrillIn("manage");
      return;
    }
    setMobileDrillIn("none");
  }, [isMobile, location.pathname]);

  useEffect(() => {
    userPrefsTabEvent(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const prev = document.title;
    document.title = "User Preferences";
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getAccountBootstrap();
        if (!mounted) return;
        setTabs(data.tabs || []);
        setProfile(data.profile);
        const formData = {
          first_name: data.profile?.first_name || "",
          last_name: data.profile?.last_name || "",
          user_height: data.profile?.user_height || "",
          user_dob: normalizeDobForDisplay(data.profile?.user_dob || ""),
          user_hand: data.profile?.user_hand || "",
          user_handicap: data.profile?.user_handicap || "",

        };
        setManageForm(formData);
        setInitialManageForm(formData);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.data?.message || "Failed to load account data.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => !tab.hidden);
  }, [tabs]);

  const manageDirty = useMemo(
    () =>
      JSON.stringify(manageForm) !== JSON.stringify(initialManageForm),
    [manageForm, initialManageForm]
  );

  const passwordReady = useMemo(() => {
    const { password, password2 } = passwordForm;
    return password.length >= 8 && password2.length >= 8 && password === password2;
  }, [passwordForm.password, passwordForm.password2]);

  const onTabClick = (tab: AccountTab) => {
    if (tab.key.startsWith("subscriptions")) {
      window.location.href = "/account/subscriptions";
      return;
    }
    if (tab.key === "password") {
      navigate("/account/password");
      if (isMobile) {
        setMobileDrillIn("password");
      }
      return;
    }
    navigate("/account/manage");
    if (isMobile) {
      setMobileDrillIn("manage");
    }
  };

  const handleMobileBack = () => {
    setMobileDrillIn("none");
    navigate("/account", { replace: true });
  };

  const clearManageError = (id: string) => {
    setManageErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const validateManageForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    REQUIRED_MANAGE_FIELDS.forEach(({ key, id }) => {
      if (!manageForm[key].trim()) {
        errors[id] = "Please fill in this field.";
      }
    });
    return errors;
  };

  const submitManage = async (e: FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!manageDirty) return;
    const errors = validateManageForm();
    if (Object.keys(errors).length > 0) {
      setManageErrors(errors);
      const firstInvalid = REQUIRED_MANAGE_FIELDS.find(({ id }) => errors[id]);
      if (firstInvalid) {
        document.getElementById(firstInvalid.id)?.focus();
      }
      return;
    }
    setManageErrors({});
    try {
      setBusyManage(true);
      const changedKeys = Object.keys(manageForm).filter(
        (key) =>
          manageForm[key as keyof typeof manageForm] !==
          initialManageForm[key as keyof typeof initialManageForm]
      );
      await updateAccountProfile(manageForm);
      setInitialManageForm(manageForm);
      setProfile((prev) => (prev ? { ...prev, ...manageForm } : prev));
      userPrefsUpdateEvent(changedKeys);
      setMessage("Profile updated successfully.");
    } catch (err: any) {
      setError(err?.data?.message || "Failed to update profile.");
    } finally {
      setBusyManage(false);
    }
  };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");
    const { password, password2 } = passwordForm;
    if (password.length < 8 || password2.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setError("Both passwords need to match.");
      return;
    }
    try {
      setBusyPassword(true);
      await updateAccountPassword(password, password2);
      userPasswordUpdateEvent("Profile");
      // Explicitly end the current session, then hard-redirect to logged-out landing.
      await fetch("/wp-json/pg/v1/user/logout", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": (window as any)?.pgData?.nonce || "",
        },
        body: JSON.stringify({}),
      });
      window.location.href = "/";
    } catch (err: any) {
      setError(err?.data?.message || "Failed to update password.");
    } finally {
      setBusyPassword(false);
    }
  };

  return (
    <ThemeProvider theme={reproTheme}>
      {loading ? (
        <Box sx={{ p: 4, textAlign: "center", fontFamily: REPRO_STACK }}>
          <CircularProgress />
        </Box>
      ) : (
    <Box sx={{  maxWidth: 1376,width: "100%", mx: "auto", fontFamily: REPRO_STACK }}>
      <Box
        component="div"
        sx={{
          px: { xs: "20px", xl: "40px" },
          pb: "40px",
          borderBottom: "1px solid #ECE9E4",
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          data-original="true"
          data-locked="true"
          separator="/"
          sx={{
            display: "none",
            [BP_MD_UP]: { display: "block" },
            "& .MuiBreadcrumbs-ol": {
              display: "none",
              [BP_MD_UP]: {
                display: "flex",
                listStyle: "none",
                alignItems: "center",
                gap: "1px",
                p: 0,
                m: 0,
                mt: "60px",
                mb: "17px",
                color: "#1D1A1A",
                fontFamily: REPRO_STACK,
                fontSize: "16px",
                lineHeight: "1.22",
                letterSpacing: "-0.16px",
              },
            },
            "& .MuiBreadcrumbs-separator": {
              p: "0 4px",
              color: "#1D1A1A",
            },
            "& .MuiBreadcrumbs-li": {
              display: "inline-flex",
              minWidth: 0,
            },
          }}
        >
          <Link
            href={getSiteHomeUrl()}
            underline="none"
            color="inherit"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
              flex: "0 0 20px",
              background: "transparent",
              border: 0,
              p: 0,
              cursor: "pointer",
              transition: "opacity .2s ease",
              "&:hover": { opacity: 0.7 },
              "&:focus-visible": {
                outline: "2px solid #1d1a1a",
                outlineOffset: "2px",
                borderRadius: "2px",
              },
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5.5 15.5H7.5V10.5H12.5V15.5H14.5V8.25L10 4.875L5.5 8.25V15.5ZM4 17V7.5L10 3L16 7.5V17H11V12H9V17H4Z"
                fill="#1D1A1A"
              />
            </svg>
            <Box component="span" sx={visuallyHidden}>
              Training
            </Box>
          </Link>
          <Typography component="span" color="inherit" sx={{ cursor: "default" }}>
            User Preferences
          </Typography>
        </Breadcrumbs>
        <Box
          component="h1"
          sx={{
            textTransform: "uppercase",
            fontSize: { xs: "36px", md: "78px" },
            lineHeight: "87%",
            fontWeight: 700,
            m: 0,
            mt: { xs: 0, md: "initial" },
          }}
        >
          User Preferences
        </Box>
      </Box>
      <Box
        id="user-prefs-mobile-back"
        sx={{
          display: isMobile && mobileDrillIn !== "none" ? "block" : "none",
          [BP_MD_UP]: { display: "none" },
          mt: "20px",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleMobileBack}
          sx={{
            color: "#FD3300",
            background: "transparent",
            fontFamily: REPRO_STACK,
            fontSize: "14px",
            lineHeight: "122%",
            fontWeight: 500,
            p: 0,
            border: 0,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            "&:hover, &:active, &:focus": {
              color: "#FD3300",
              background: "transparent",
            },
          }}
        >
          <img
            src="/wp-content/themes/pgm/dist/images/icons/orange-left-arrow.svg"
            alt="back"
            width={16}
            height={16}
          />
          <Box component="span" sx={{ ml: "10px" }}>
            Return to User Preferences Menu
          </Box>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
      <Box
        sx={{
          px: 0,
          mt: { xs: "12px", md: "32px" },
          display: "flex",
          flexDirection: "column",
          [BP_MD_UP]: {
            flexDirection: "row",
          },
        }}
      >
        <Box
          sx={{
            display: isMobile && mobileDrillIn !== "none" ? "none" : "block",
            width: { xs: "100%" },
            minWidth: { xs: "100%" },
            maxWidth: { xs: "100%" },
            flexShrink: 0,
            borderRight: { xs: "none" },
            pt: { xs: 0 },
            [BP_MD_UP]: {
              display: "block",
              width: "256px",
              minWidth: "256px",
              maxWidth: "256px",
              borderRight: "1px solid rgba(220, 220, 220, 0.45)",
              pt: "10px",
            },
          }}
        >
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              mb: 0,
            }}
          >
            {visibleTabs.map((tab) => {
              const selected =
                (activeTab === "manage" && tab.key === "manage") ||
                (activeTab === "password" && tab.key === "password");
              return (
                <Box
                  component="li"
                  key={tab.key}
                  sx={{
                    "&:not(:last-of-type)": { mb: "7px" },
                  }}
                >
                  <Box
                    component="a"
                    href="#"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      onTabClick(tab);
                    }}
                    sx={{
                      py: { xs: "16px" },
                      pl: { xs: 0 },
                      pr: { xs: 0 },
                      [BP_MD_UP]: { py: "12px", pl: 0, pr: "20px" },
                      fontWeight: selected ? 800 : 400,
                      fontSize: "16px",
                      lineHeight: { xs: "122%", md: "19px" },
                      letterSpacing: "-0.03px",
                      color: "#000",
                      transition: "background .3s",
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                      textDecoration: "none !important",
                      cursor: "pointer",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        display: "block",
                        height: "100%",
                        width: "2px",
                        background: "#355ADD",
                        opacity: 0,
                        transition: "opacity .3s",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        right: "23px",
                        width: "8px",
                        height: "13px",
                        transform: "translateY(-50%)",
                        background:
                          'url("/wp-content/themes/pgm/dist/images/icons/arrow-right.svg") center center no-repeat',
                      },
                      "&:hover": {
                        textDecoration: "none !important",
                        color: "#000",
                      },
                      "&:focus, &:active, &:visited": {
                        textDecoration: "none !important",
                        color: "#000",
                      },
                      "&:hover::after": {
                        background:
                          'url("/wp-content/themes/pgm/dist/images/icons/arrow-right-high.svg") center center no-repeat',
                      },
                    }}
                  >
                    <img
                      src={`/wp-content/themes/pgm/dist/images/icons/${tab.icon}.svg`}
                      alt={tab.name}
                     style={{
    width: 16,
    height: 16,
    display: "block",
    objectFit: "contain",
  }}
                    />
                    <Box component="div" sx={{ ml: "16px" }}>
                      {tab.name}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            pt: "15px",
            pb: "60px",
            [BP_MD_UP]: {
              pt: "40px",
              pl: "40px",
              pb: "100px",
            },
            ...(isMobile && mobileDrillIn === "none"
              ? { display: "none", [BP_MD_UP]: { display: "block" } }
              : {}),
          }}
        >
          {activeTab === "manage" && profile && (
            <Box component="form" noValidate onSubmit={submitManage}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: "36px",
                  [BP_MD_UP]: { mb: 0 },
                }}
              >
                <Box
                  component="img"
                  src={resolveMediaUrl(profile.avatar)}
                  alt="avatar"
                  width={75}
                  height={75}
                  sx={{
                    objectFit: "cover",
                    borderRadius: "50%",
                    [BP_MD_UP]: { position: "relative", bottom: "5px" },
                  }}
                />
                <Box sx={{ pl: "16px" }}>
                  <Box
                    component="div"
                    sx={{
                      fontSize: "24px",
                      lineHeight: "120%",
                      fontWeight: 700,
                    }}
                  >
                    {`${manageForm.first_name} ${manageForm.last_name}`.trim()}
                  </Box>
                  <Box
                    component="a"
                    href="#"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      setAvatarModalNonce((n) => n + 1);
                      setOpenAvatar(true);
                    }}
                    sx={{
                      fontSize: "14px",
                      lineHeight: "120%",
                      color: "#FD3300",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Update Profile Picture
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  borderTop: "1px solid rgba(220, 220, 220, 0.45)",
                  [BP_MD_UP]: { mt: "35px", pt: "40px" },
                }}
              >
                <Box
                  component="div"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    mb: "24px",
                    mt: { xs: "40px", md: 0 },
                    fontSize: "18px",
                    fontWeight: 700,
                    lineHeight: "122%",
                    letterSpacing: "0.02em",
                    textAlign: "left",
                  }}
                >
                  <Box component="span" sx={{ color: "#171B32" }}>
                    Email Address:
                  </Box>
                  <Box component="div" sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <img src="/wp-content/themes/pgm/dist/images/icons/gray-lock.svg" alt="locked" width={16} height={16} />
                    <Box component="span" sx={{ color: "#6F6F70", fontSize: "14px", fontWeight: 400 }}>
                      {profile.email}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    width: "100%",
                    boxSizing: "border-box",
                    columnGap: { xs: 0 },
                    rowGap: "24px",
                    gridTemplateColumns: { xs: "1fr" },
                    [BP_LG_UP]: {
                      columnGap: "24px",
                      gridTemplateColumns: "1fr 1fr",
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <CornerOutlineField
                      id="account-first-name"
                      label="First Name"
                      value={manageForm.first_name}
                      onChange={(first_name) => {
                        setManageForm((prev) => ({ ...prev, first_name }));
                        clearManageError("account-first-name");
                      }}
                      required
                      error={Boolean(manageErrors["account-first-name"])}
                      helperText={manageErrors["account-first-name"]}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <CornerOutlineField
                      id="account-last-name"
                      label="Last Name"
                      value={manageForm.last_name}
                      onChange={(last_name) => {
                        setManageForm((prev) => ({ ...prev, last_name }));
                        clearManageError("account-last-name");
                      }}
                      required
                      error={Boolean(manageErrors["account-last-name"])}
                      helperText={manageErrors["account-last-name"]}
                    />
                  </Box>
<Box
  sx={{
    display: "flex",
    gap: 2,
    minWidth: 0,

    "@media (max-width:375px)": {
      gap: 1,
    },

    // 376px – 390px
    "@media (min-width:376px) and (max-width:390px)": {
      gap: 2,
    },

    // 391px – 425px
    "@media (min-width:391px) and (max-width:425px)": {
      gap: 6,
    },

    // 426px – 767px
    "@media (min-width:426px) and (max-width:767px)": {
      gap: 2,
    },

    // 768px+
    "@media (min-width:768px)": {
      justifyContent: "space-between",
      gap: 2,
    },
  }}
>
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <CornerOutlineField
      id="account-height"
      label="Height (FEET)"
      select
      value={feetValue}
      onChange={(user_height) => handleFeetChange(user_height)}
    >
      {feetOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </CornerOutlineField>
  </Box>

  <Box sx={{ flex: 1, minWidth: 0 }}>
    <CornerOutlineField
      id="account-height"
      label="Height (INCHES)"
      select
      value={inchesValue}
      onChange={(user_height) => handleInchesChange(user_height)}
    >
      {inchesOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </CornerOutlineField>
  </Box>
</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <CornerOutlineField
                      id="account-dob"
                      label="Date of Birth"
                      datePicker
                      value={manageForm.user_dob}
                      onChange={(user_dob) => {
                        setManageForm((prev) => ({ ...prev, user_dob }));
                        clearManageError("account-dob");
                      }}
                      required
                      error={Boolean(manageErrors["account-dob"])}
                      helperText={manageErrors["account-dob"]}
                    />
                  </Box>
                  <Box
                   sx={{ minWidth: 0 }}
                    // sx={{
                    //   gridColumn: { xs: "auto" },
                    //   [BP_LG_UP]: { gridColumn: "span 2" },
                    //   minWidth: 0,
                    // }}
                  >
                    <CornerOutlineField
                      id="account-hand"
                      label="Hand"
                      value={manageForm.user_hand}
                      onChange={(user_hand) => {
                        setManageForm((prev) => ({ ...prev, user_hand }));
                        clearManageError("account-hand");
                      }}
                      required
                      error={Boolean(manageErrors["account-hand"])}
                      helperText={manageErrors["account-hand"]}
                      select
                    >
                      {handOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </CornerOutlineField>
                  </Box>

                    <Box
                   sx={{ minWidth: 0 }}
                  >
                    <CornerOutlineField
                      id="account-handicap"
                      label="Handicap"
                      value={manageForm.user_handicap}
                      onChange={(user_handicap) => {
                        setManageForm((prev) => ({ ...prev, user_handicap }));
                        clearManageError("account-handicap");
                      }}
                      required
                      error={Boolean(manageErrors["account-handicap"])}
                      helperText={manageErrors["account-handicap"]}
                      select
                    >
                      {handicapOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </CornerOutlineField>
                  </Box>
                  <Box
                    sx={{
                      gridColumn: { xs: "auto" },
                      [BP_LG_UP]: { gridColumn: "span 2" },
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!manageDirty || busyManage}
                      sx={{
                        width: "88px",
                        backgroundColor: "#FD3300",
                        borderColor: "#FD3300",
                        borderRadius: "9999px",
                        minHeight: 48,
                        height: 48,
                        fontFamily: REPRO_STACK,
                        fontSize: "16px",
                        fontWeight: 700,
                        lineHeight: "19px",
                        textTransform: "none",
                        px: "20px",
                        "&:hover": {
                          backgroundColor: "#DB2C00",
                          borderColor: "#DB2C00",
                        },
                        "&.Mui-disabled": {
                          opacity: 1,
                          color: "#7b726c",
                          WebkitTextFillColor: "#7b726c",
                          backgroundColor: "#b3aaa3",
                          borderColor: "#b3aaa3",
                        },
                      }}
                    >
                      {busyManage ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === "password" && (
            <Box component="form" onSubmit={submitPassword}>
              <Typography
                component="div"
                sx={{
                  mb: "25px",
                  fontSize: "14px",
                  lineHeight: "17px",
                  fontWeight: 800,
                  [BP_MD_UP]: {
                    fontSize: "16px",
                    lineHeight: "19px",
                  },
                }}
              >
                Change Password
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    pr: { xs: 0 },
                    boxSizing: "border-box",
                    mb: "15px",
                    [BP_MD_UP]: { width: "50%", mb: "25px", pr: "8px" },
                  }}
                >
                  <CornerPasswordField
                    id="account-new-password"
                    label="New Password *"
                    value={passwordForm.password}
                    onChange={(password) => setPasswordForm((prev) => ({ ...prev, password }))}
                    show={showNewPassword}
                    onToggleShow={() => setShowNewPassword((v) => !v)}
                    showValid={passwordForm.password.length >= 8}
                  />
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    pl: { xs: 0 },
                    boxSizing: "border-box",
                    mb: "15px",
                    [BP_MD_UP]: { width: "50%", mb: "25px", pl: "8px" },
                  }}
                >
                  <CornerPasswordField
                    id="account-confirm-password"
                    label="Confirm New Password *"
                    value={passwordForm.password2}
                    onChange={(password2) => setPasswordForm((prev) => ({ ...prev, password2 }))}
                    show={showConfirmPassword}
                    onToggleShow={() => setShowConfirmPassword((v) => !v)}
                    showValid={
                      passwordForm.password.length >= 8 &&
                      passwordForm.password2.length >= 8 &&
                      passwordForm.password === passwordForm.password2
                    }
                  />
                </Box>
                <Box
                  sx={{
                    display: "none",
                    [BP_MD_UP]: { display: "block", width: "50%", mb: "25px" },
                  }}
                />
                <Box
                  sx={{
                    width: "100%",
                    textAlign: "right",
                    mb: "15px",
                    [BP_MD_UP]: { width: "50%", mb: "25px" },
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={busyPassword || !passwordReady}
                    sx={{
                      borderRadius: "100px",
                      minHeight: 48,
                      height: 48,
                      py: "10px",
                      px: "20px",
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: "19px",
                      textTransform: "none",
                      boxShadow: "none",
                      borderWidth: "2px",
                      borderStyle: "solid",
                      borderColor: "#FD3300",
                      backgroundColor: "#FD3300",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#DB2C00",
                        borderColor: "#DB2C00",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        opacity: 1,
                        boxShadow: "none",
                        ...(busyPassword
                          ? {
                              backgroundColor: "#FD3300",
                              color: "#fff",
                              WebkitTextFillColor: "#fff",
                              borderColor: "#FD3300",
                            }
                          : {
                              backgroundColor: "rgb(179, 170, 163)",
                              color: "rgb(123, 114, 108)",
                              WebkitTextFillColor: "rgb(123, 114, 108)",
                              borderColor: "rgb(179, 170, 163)",
                              cursor: "no-drop",
                            }),
                      },
                    }}
                  >
                    {busyPassword ? "Updating..." : "Update Password"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {profile && (
        <AvatarEditorDialog
          key={openAvatar ? `avatar-open:${avatarModalNonce}` : "avatar-closed"}
          open={openAvatar}
          avatar={profile.avatar}
          onClose={() => setOpenAvatar(false)}
          onSaved={(avatarUrl) => setProfile((prev) => (prev ? { ...prev, avatar: avatarUrl } : prev))}
        />
      )}
    </Box>
      )}
    </ThemeProvider>
  );
}
